// frontend/BookingComments.jsx
// Admin page: booking comments submitted by agents, viewed agent-wise.
// Needs: react (18+). No other dependencies. Styles are embedded below.
//
// Routes used (see AgentRoutes.js):
//   GET /api/agent/booking-comments/summary
//   GET /api/agent/booking-comments?user_id=&search=&status=&from=&to=&page=&limit=
//   PUT /api/agent/booking-comments/:id/read
//   PUT /api/agent/booking-comments/read-all

import { useCallback, useEffect, useMemo, useState } from "react";

//const API_BASE = (import.meta.env && import.meta.env.VITE_API_URL) || "/api";
// const API_BASE = (import.meta.env && import.meta.env.VITE_API_URL) || "/api/v1";
// const PAGE_SIZE = 20;

// /* ----------------------------- helpers ----------------------------- */

// const authHeaders = () => {
//   const token = localStorage.getItem("token");
//   return {
//     "Content-Type": "application/json",
//     ...(token ? { Authorization: `Bearer ${token}` } : {}),
//   };
// };

// async function api(path, options = {}) {
//   const res = await fetch(`${API_BASE}${path}`, {
//     ...options,
//     headers: { ...authHeaders(), ...(options.headers || {}) },
//   });
//   const json = await res.json().catch(() => ({}));
//   if (!res.ok || json.success === false) {
//     throw new Error(json.message || "Something went wrong");
//   }
//   return json;
// }
const API_BASE = process.env.REACT_APP_API_URL || "/api/v1";
const PAGE_SIZE = 20;

/* ----------------------------- DEBUG ----------------------------- */
const DEBUG = true; // set to false when done

const log = (...a) =>
  DEBUG && console.log("%c[BookingComments]", "color:#7B4DFF;font-weight:bold", ...a);

if (DEBUG) {
  console.group("%c[BookingComments] startup", "color:#7B4DFF;font-weight:bold");
  console.log("API_BASE            :", API_BASE);

  console.log("Page origin         :", window.location.origin);
  console.log(
    "Summary URL resolves to:",
    new URL(`${API_BASE}/agent/booking-comments/summary`, window.location.origin).href
  );
  console.log("Token in localStorage:", localStorage.getItem("token") ? "yes" : "NO");
  console.groupEnd();
}

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function api(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const method = options.method || "GET";
  const absolute = new URL(url, window.location.origin).href;
  const t0 = performance.now();

  log("→ REQUEST", method, absolute, options.body ? { body: options.body } : "");

  let res;
  try {
    res = await fetch(url, {
      ...options,
      headers: { ...authHeaders(), ...(options.headers || {}) },
    });
  } catch (netErr) {
    // fetch only throws for network failure, CORS block, or server down
    console.error("✖ NETWORK/CORS ERROR", method, absolute, netErr);
    console.error(
      "  → Server not running, wrong port, or blocked by CORS. " +
        "Check ALLOWED_ORIGINS in .env includes", window.location.origin
    );
    throw new Error(`Cannot reach server at ${absolute} (network or CORS error)`);
  }

  const ms = Math.round(performance.now() - t0);
  const raw = await res.text();

  log("← RESPONSE", res.status, res.statusText, `${ms}ms`, {
    url: res.url,
    redirected: res.redirected,
    contentType: res.headers.get("content-type"),
    bodyPreview: raw.slice(0, 400),
  });

  let json;
  try {
    json = JSON.parse(raw);
  } catch {
    console.error("✖ RESPONSE IS NOT JSON (HTTP " + res.status + ") from", absolute);
    console.error("  → Usually means the request hit the React dev server or a wrong route (HTML 404 page).");
    console.error("  Body starts with:", raw.slice(0, 200));
    throw new Error(`Server returned non-JSON (HTTP ${res.status}) from ${absolute}`);
  }

  if (!res.ok || json.success === false) {
    console.error("✖ API ERROR", res.status, json);
    throw new Error(json.message || `Request failed (HTTP ${res.status})`);
  }

  return json;
}
const PALETTE = [
  { bg: "#E6E3FF", fg: "#3B2FD0" }, // indigo
  { bg: "#D5F5F1", fg: "#0B7F75" }, // teal
  { bg: "#FFE3DF", fg: "#C7402F" }, // coral
  { bg: "#FFF0CC", fg: "#9A6700" }, // amber
  { bg: "#F7DDF5", fg: "#A024A0" }, // magenta
  { bg: "#DDEEFF", fg: "#1D5FB4" }, // blue
];
const colorFor = (id) => PALETTE[Math.abs(Number(id) || 0) % PALETTE.length];

const initials = (name, id) => {
  if (!name) return "A";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || String(id).slice(0, 2);
};

const toDate = (s) => new Date(String(s).replace(" ", "T"));

const dayLabel = (s) => {
  const d = toDate(s);
  const today = new Date();
  const y = new Date();
  y.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "Today";
  if (same(d, y)) return "Yesterday";
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

const timeLabel = (s) =>
  toDate(s).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });

// Pull a booking reference such as PT-26-27-02400 out of the comment text.
const findRef = (text) => {
  const m = /\b[A-Za-z]{2,4}-\d{2}-\d{2}-\d{3,6}\b/.exec(text || "");
  return m ? m[0].toUpperCase() : null;
};

const agentLabel = (a) => a.agent_name || `Agent #${a.user_id}`;

/* ----------------------------- component ----------------------------- */

export default function BookingComments() {
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(true);
  const [selected, setSelected] = useState(null); // null = all agents
  const [agentFilter, setAgentFilter] = useState("");

  const [comments, setComments] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);

  /* debounce search */
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const loadAgents = useCallback(async () => {
    try {
      const json = await api("/agent/booking-comments/summary");
      setAgents(json.data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  const loadComments = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams({ page, limit: PAGE_SIZE, status });
      if (selected) qs.set("user_id", selected);
      if (search) qs.set("search", search);
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      const json = await api(`/agent/booking-comments?${qs}`);
      setComments(json.data || []);
      setPagination(json.pagination || { page: 1, pages: 1, total: 0 });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, status, selected, search, from, to]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const totals = useMemo(
    () =>
      agents.reduce(
        (t, a) => ({
          comments: t.comments + Number(a.total_comments),
          unread: t.unread + Number(a.unread_comments),
          bookings: t.bookings + Number(a.bookings),
        }),
        { comments: 0, unread: 0, bookings: 0 }
      ),
    [agents]
  );

  const selectedAgent = agents.find((a) => a.user_id === selected);

  const visibleAgents = agents.filter((a) =>
    agentLabel(a).toLowerCase().includes(agentFilter.toLowerCase()) ||
    String(a.user_id).includes(agentFilter)
  );

  const grouped = useMemo(() => {
    const map = new Map();
    comments.forEach((c) => {
      const k = dayLabel(c.created_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(c);
    });
    return [...map.entries()];
  }, [comments]);

  const selectAgent = (id) => {
    setSelected(id);
    setPage(1);
  };

  const markRead = async (c) => {
    if (c.is_read) return;
    setComments((list) => list.map((x) => (x.comment_id === c.comment_id ? { ...x, is_read: 1 } : x)));
    try {
      await api(`/agent/booking-comments/${c.comment_id}/read`, { method: "PUT" });
      loadAgents();
    } catch (e) {
      setError(e.message);
      loadComments();
    }
  };

  const markAllRead = async () => {
    try {
      await api("/agent/booking-comments/read-all", {
        method: "PUT",
        body: JSON.stringify(selected ? { user_id: selected } : {}),
      });
      loadAgents();
      loadComments();
    } catch (e) {
      setError(e.message);
    }
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatus("all");
    setFrom("");
    setTo("");
    setPage(1);
  };

  const hasFilters = search || status !== "all" || from || to;
  const unreadInView = selectedAgent ? Number(selectedAgent.unread_comments) : totals.unread;

  return (
    <div className="bc">
      <style>{css}</style>

      {/* ------------------------------ header ------------------------------ */}
      <header className="bc-head">
        <div className="bc-head-text">
          <h1>Booking comments</h1>
          <p>Notes agents have left on their bookings, grouped by agent.</p>
        </div>
        <div className="bc-stats">
          <Stat tone="violet" value={agents.length} label="Agents commenting" />
          <Stat tone="teal" value={totals.comments} label="Total comments" />
          <Stat tone="amber" value={totals.bookings} label="Bookings with notes" />
          <Stat tone="coral" value={totals.unread} label="Unread" />
        </div>
      </header>

      <div className="bc-body">
        {/* ------------------------------ agent rail ------------------------------ */}
        <aside className="bc-rail" aria-label="Agents">
          <div className="bc-rail-top">
            <input
              className="bc-input"
              type="search"
              placeholder="Find an agent"
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              aria-label="Find an agent"
            />
          </div>

          <ul className="bc-agent-list">
            <li>
              <button
                className={`bc-agent ${selected === null ? "is-active" : ""}`}
                onClick={() => selectAgent(null)}
              >
                <span className="bc-avatar all">All</span>
                <span className="bc-agent-main">
                  <strong>All agents</strong>
                  <small>{totals.comments} comments</small>
                </span>
                {totals.unread > 0 && <span className="bc-badge">{totals.unread}</span>}
              </button>
            </li>

            {agentsLoading &&
              [1, 2, 3, 4].map((i) => (
                <li key={i} className="bc-skel-row">
                  <span className="bc-skel circle" />
                  <span className="bc-skel line" />
                </li>
              ))}

            {!agentsLoading && visibleAgents.length === 0 && (
              <li className="bc-rail-empty">No agents match “{agentFilter}”.</li>
            )}

            {visibleAgents.map((a) => {
              const col = colorFor(a.user_id);
              return (
                <li key={a.user_id}>
                  <button
                    className={`bc-agent ${selected === a.user_id ? "is-active" : ""}`}
                    onClick={() => selectAgent(a.user_id)}
                    style={{ "--agent-fg": col.fg, "--agent-bg": col.bg }}
                  >
                    <span className="bc-avatar" style={{ background: col.bg, color: col.fg }}>
                      {initials(a.agent_name, a.user_id)}
                    </span>
                    <span className="bc-agent-main">
                      <strong>{agentLabel(a)}</strong>
                      <small>
                        {a.company_name ? `${a.company_name} · ` : ""}
                        {a.total_comments} {Number(a.total_comments) === 1 ? "comment" : "comments"}
                      </small>
                    </span>
                    {Number(a.unread_comments) > 0 && (
                      <span className="bc-badge">{a.unread_comments}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* ------------------------------ main ------------------------------ */}
        <main className="bc-main">
          {/* agent banner */}
          <section
            className="bc-banner"
            style={
              selectedAgent
                ? { "--b-bg": colorFor(selectedAgent.user_id).bg, "--b-fg": colorFor(selectedAgent.user_id).fg }
                : { "--b-bg": "#E6E3FF", "--b-fg": "#3B2FD0" }
            }
          >
            <span className="bc-avatar lg">
              {selectedAgent ? initials(selectedAgent.agent_name, selectedAgent.user_id) : "All"}
            </span>
            <div className="bc-banner-text">
              <h2>{selectedAgent ? agentLabel(selectedAgent) : "All agents"}</h2>
              <p>
                {selectedAgent
                  ? `${selectedAgent.company_name ? selectedAgent.company_name + " · " : ""}Agent ID ${selectedAgent.user_id} · Last comment ${dayLabel(selectedAgent.last_comment_at)}, ${timeLabel(selectedAgent.last_comment_at)}`
                  : "Showing the latest comments from every agent."}
              </p>
            </div>
            <button className="bc-btn ghost" onClick={markAllRead} disabled={unreadInView === 0}>
              Mark {unreadInView || ""} as read
            </button>
          </section>

          {/* filters */}
          <section className="bc-filters">
            <div className="bc-search">
              <input
                className="bc-input"
                type="search"
                placeholder="Search comment, booking number or agent"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                aria-label="Search comments"
              />
            </div>

            <div className="bc-seg" role="tablist" aria-label="Read status">
              {[
                ["all", "All"],
                ["unread", "Unread"],
                ["read", "Read"],
              ].map(([v, l]) => (
                <button
                  key={v}
                  role="tab"
                  aria-selected={status === v}
                  className={status === v ? "is-on" : ""}
                  onClick={() => {
                    setStatus(v);
                    setPage(1);
                  }}
                >
                  {l}
                </button>
              ))}
            </div>

            <label className="bc-date">
              <span>From</span>
              <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
            </label>
            <label className="bc-date">
              <span>To</span>
              <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
            </label>

            {hasFilters && (
              <button className="bc-btn link" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </section>

          {error && (
            <div className="bc-alert" role="alert">
              <span>{error}</span>
              <button className="bc-btn link" onClick={loadComments}>
                Try again
              </button>
            </div>
          )}

          {/* list */}
          <section className="bc-list" aria-busy={loading}>
            {loading &&
              [1, 2, 3].map((i) => (
                <div key={i} className="bc-card skeleton">
                  <span className="bc-skel circle" />
                  <div className="bc-skel-col">
                    <span className="bc-skel line w40" />
                    <span className="bc-skel line w80" />
                  </div>
                </div>
              ))}

            {!loading && !error && comments.length === 0 && (
              <div className="bc-empty">
                <div className="bc-empty-mark">0</div>
                <h3>{hasFilters ? "No comments match these filters" : "No comments yet"}</h3>
                <p>
                  {hasFilters
                    ? "Change or clear the filters to see more comments."
                    : "When an agent adds a note to a booking, it will show up here."}
                </p>
                {hasFilters && (
                  <button className="bc-btn solid" onClick={clearFilters}>
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {!loading &&
              grouped.map(([label, items]) => (
                <div key={label} className="bc-day">
                  <h3 className="bc-day-label">{label}</h3>
                  {items.map((c) => {
                    const col = colorFor(c.user_id);
                    const ref = findRef(c.comment_text);
                    const unread = !c.is_read;
                    return (
                      <article
                        key={c.comment_id}
                        className={`bc-card ${unread ? "is-unread" : ""}`}
                        style={{ "--stripe": col.fg }}
                      >
                        <span className="bc-avatar" style={{ background: col.bg, color: col.fg }}>
                          {initials(c.agent_name, c.user_id)}
                        </span>

                        <div className="bc-card-body">
                          <div className="bc-card-top">
                            <button
                              className="bc-name"
                              onClick={() => selectAgent(c.user_id)}
                              title="Show only this agent"
                            >
                              {c.agent_name || `Agent #${c.user_id}`}
                            </button>
                            {c.company_name && <span className="bc-company">{c.company_name}</span>}
                            <span className="bc-time">{timeLabel(c.created_at)}</span>
                          </div>

                          <p className="bc-text">{c.comment_text}</p>

                          <div className="bc-card-foot">
                            <span className="bc-chip violet">Enquiry #{c.enq_id}</span>
                            {ref && <span className="bc-chip teal">{ref}</span>}
                            <span className="bc-chip muted">Comment #{c.comment_id}</span>
                            <span className="bc-spacer" />
                            {unread ? (
                              <button className="bc-btn tiny" onClick={() => markRead(c)}>
                                Mark as read
                              </button>
                            ) : (
                              <span className="bc-read">Read</span>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ))}
          </section>

          {/* pagination */}
          {pagination.pages > 1 && (
            <nav className="bc-pager" aria-label="Pagination">
              <span>
                Page {pagination.page} of {pagination.pages} · {pagination.total} comments
              </span>
              <div>
                <button className="bc-btn ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </button>
                <button
                  className="bc-btn solid"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </nav>
          )}
        </main>
      </div>
    </div>
  );
}

function Stat({ tone, value, label }) {
  return (
    <div className={`bc-stat ${tone}`}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

/* ----------------------------- styles ----------------------------- */

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.bc {
  --ink: #1B1A3C;
  --ink-2: #55557A;
  --line: #E4E5F2;
  --bg: #F4F5FC;
  --card: #FFFFFF;
  --indigo: #3B2FD0;
  --indigo-d: #2A20A8;
  --teal: #0FB5A6;
  --coral: #FF6B5B;
  --amber: #FFB020;
  --violet: #7B4DFF;
  font-family: 'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', sans-serif;
  color: var(--ink);
  background: var(--bg);
  min-height: 100vh;
  font-size: 14px;
  line-height: 1.5;
}
.bc *, .bc *::before, .bc *::after { box-sizing: border-box; }
.bc h1, .bc h2, .bc h3, .bc p { margin: 0; }
.bc button { font-family: inherit; cursor: pointer; }
.bc button:disabled { cursor: not-allowed; opacity: .5; }
.bc :focus-visible { outline: 3px solid #A99BFF; outline-offset: 2px; }

/* header */
.bc-head {
  background: var(--indigo);
  color: #fff;
  padding: 28px 32px 56px;
  display: flex;
  flex-wrap: wrap;
  gap: 24px 40px;
  align-items: flex-end;
  justify-content: space-between;
}
.bc-head h1 { font-size: 30px; font-weight: 800; letter-spacing: -0.02em; }
.bc-head p { margin-top: 4px; color: #D3CFFF; max-width: 46ch; }
.bc-stats { display: grid; grid-template-columns: repeat(4, minmax(120px, 1fr)); gap: 12px; flex: 1 1 520px; max-width: 700px; }
.bc-stat { border-radius: 14px; padding: 14px 16px; display: flex; flex-direction: column; gap: 2px; color: #fff; }
.bc-stat strong { font-size: 28px; font-weight: 800; line-height: 1.1; }
.bc-stat span { font-size: 12.5px; font-weight: 600; opacity: .92; }
.bc-stat.violet { background: var(--violet); }
.bc-stat.teal { background: #0A9488; }
.bc-stat.amber { background: #E39200; }
.bc-stat.coral { background: #E8503F; }

/* layout */
.bc-body {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 20px;
  padding: 0 32px 40px;
  margin-top: -32px;
  align-items: start;
}

/* rail */
.bc-rail {
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
}
.bc-rail-top { padding: 14px; border-bottom: 1px solid var(--line); }
.bc-agent-list { list-style: none; margin: 0; padding: 8px; overflow-y: auto; }
.bc-agent {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border: 0;
  background: transparent;
  border-radius: 12px;
  text-align: left;
  color: var(--ink);
  transition: background .15s;
}
.bc-agent:hover { background: #F1F1FB; }
.bc-agent.is-active { background: var(--agent-bg, #E6E3FF); box-shadow: inset 4px 0 0 var(--agent-fg, var(--indigo)); }
.bc-agent-main { display: flex; flex-direction: column; min-width: 0; flex: 1; }
.bc-agent-main strong { font-weight: 700; font-size: 14px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bc-agent-main small { color: var(--ink-2); font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.bc-badge { background: var(--coral); color: #fff; font-weight: 800; font-size: 12px; min-width: 24px; height: 24px; padding: 0 7px; border-radius: 12px; display: grid; place-items: center; }
.bc-rail-empty { padding: 16px; color: var(--ink-2); }

.bc-avatar {
  width: 40px; height: 40px; flex: none;
  border-radius: 12px;
  display: grid; place-items: center;
  font-weight: 800; font-size: 14px;
}
.bc-avatar.all { background: var(--indigo); color: #fff; }
.bc-avatar.lg { width: 56px; height: 56px; border-radius: 16px; font-size: 18px; background: var(--b-fg); color: #fff; }

/* banner */
.bc-banner {
  display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
  background: var(--b-bg);
  border-radius: 16px;
  padding: 18px 20px;
}
.bc-banner-text { flex: 1 1 260px; min-width: 0; }
.bc-banner h2 { font-size: 20px; font-weight: 800; color: var(--b-fg); letter-spacing: -0.01em; }
.bc-banner p { color: var(--ink-2); font-size: 13px; margin-top: 2px; }

/* filters */
.bc-filters {
  display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 14px;
  margin-top: 14px;
}
.bc-search { flex: 1 1 260px; }
.bc-input, .bc-date input {
  width: 100%; height: 40px;
  border: 1.5px solid var(--line);
  border-radius: 10px;
  padding: 0 12px;
  font: inherit; color: var(--ink); background: #fff;
}
.bc-input:focus, .bc-date input:focus { border-color: var(--indigo); outline: none; box-shadow: 0 0 0 3px #DAD6FF; }
.bc-date { display: flex; flex-direction: column; gap: 2px; font-size: 12px; font-weight: 600; color: var(--ink-2); }
.bc-date input { width: 150px; }

.bc-seg { display: inline-flex; background: #EEEEF8; border-radius: 10px; padding: 3px; height: 40px; }
.bc-seg button { border: 0; background: transparent; padding: 0 14px; border-radius: 8px; font-weight: 700; color: var(--ink-2); }
.bc-seg button.is-on { background: var(--indigo); color: #fff; }

/* buttons */
.bc-btn { height: 40px; padding: 0 16px; border-radius: 10px; font-weight: 700; font-size: 13.5px; border: 1.5px solid transparent; }
.bc-btn.solid { background: var(--indigo); color: #fff; }
.bc-btn.solid:hover:not(:disabled) { background: var(--indigo-d); }
.bc-btn.ghost { background: #fff; color: var(--indigo); border-color: var(--indigo); }
.bc-btn.ghost:hover:not(:disabled) { background: #F0EEFF; }
.bc-btn.link { background: transparent; color: var(--indigo); padding: 0 8px; }
.bc-btn.tiny { height: 30px; padding: 0 12px; font-size: 12.5px; background: #fff; color: var(--indigo); border-color: #C9C4FF; }
.bc-btn.tiny:hover { background: #F0EEFF; }

/* alert */
.bc-alert { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 14px; padding: 12px 16px; border-radius: 12px; background: #FFE9E6; color: #A32A1B; font-weight: 600; }

/* list */
.bc-list { margin-top: 8px; }
.bc-day { margin-top: 18px; }
.bc-day-label { font-size: 13px; font-weight: 700; color: var(--ink-2); margin-bottom: 10px; display: flex; align-items: center; gap: 12px; }
.bc-day-label::after { content: ""; flex: 1; height: 1px; background: var(--line); }

.bc-card {
  display: flex; gap: 14px;
  background: var(--card);
  border: 1px solid var(--line);
  border-left: 5px solid var(--stripe, var(--line));
  border-radius: 14px;
  padding: 16px;
  margin-bottom: 10px;
}
.bc-card.is-unread { background: #FFFDF6; border-color: #F5E4B3; border-left-color: var(--stripe); box-shadow: 0 1px 0 #F5E4B3; }
.bc-card-body { flex: 1; min-width: 0; }
.bc-card-top { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 10px; }
.bc-name { border: 0; background: none; padding: 0; font-weight: 800; font-size: 15px; color: var(--ink); }
.bc-name:hover { color: var(--indigo); text-decoration: underline; }
.bc-company { color: var(--ink-2); font-size: 12.5px; }
.bc-time { margin-left: auto; color: var(--ink-2); font-size: 12.5px; font-weight: 600; }
.bc-text { margin-top: 6px; font-size: 15px; color: var(--ink); overflow-wrap: anywhere; white-space: pre-wrap; }
.bc-card-foot { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 12px; }
.bc-spacer { flex: 1; }
.bc-chip { font-size: 12px; font-weight: 700; padding: 3px 10px; border-radius: 999px; }
.bc-chip.violet { background: #ECE7FF; color: #4A2FD0; }
.bc-chip.teal { background: #D5F5F1; color: #0B7F75; }
.bc-chip.muted { background: #F0F0F7; color: var(--ink-2); }
.bc-read { font-size: 12.5px; font-weight: 700; color: #0B7F75; }

/* empty + skeleton */
.bc-empty { text-align: center; padding: 56px 20px; background: var(--card); border: 1px dashed #C9CBE6; border-radius: 16px; margin-top: 18px; }
.bc-empty-mark { width: 64px; height: 64px; margin: 0 auto 14px; border-radius: 20px; background: #FFF0CC; color: #9A6700; display: grid; place-items: center; font-size: 26px; font-weight: 800; }
.bc-empty h3 { font-size: 18px; font-weight: 800; }
.bc-empty p { color: var(--ink-2); margin: 6px auto 16px; max-width: 40ch; }

.bc-skel { background: linear-gradient(90deg, #ECECF7 25%, #F6F6FC 50%, #ECECF7 75%); background-size: 200% 100%; animation: bc-shimmer 1.3s infinite; border-radius: 8px; display: block; }
.bc-skel.circle { width: 40px; height: 40px; border-radius: 12px; flex: none; }
.bc-skel.line { height: 12px; }
.bc-skel.w40 { width: 40%; } .bc-skel.w80 { width: 80%; }
.bc-skel-col { flex: 1; display: flex; flex-direction: column; gap: 10px; padding-top: 6px; }
.bc-skel-row { display: flex; align-items: center; gap: 12px; padding: 10px; }
.bc-skel-row .line { flex: 1; }
@keyframes bc-shimmer { to { background-position: -200% 0; } }

/* pager */
.bc-pager { display: flex; flex-wrap: wrap; gap: 12px; justify-content: space-between; align-items: center; margin-top: 18px; color: var(--ink-2); font-weight: 600; }
.bc-pager div { display: flex; gap: 8px; }

/* responsive */
@media (max-width: 980px) {
  .bc-head { padding: 24px 18px 52px; }
  .bc-stats { grid-template-columns: repeat(2, 1fr); }
  .bc-body { grid-template-columns: 1fr; padding: 0 18px 32px; }
  .bc-rail { position: static; max-height: none; }
  .bc-agent-list { display: flex; gap: 8px; overflow-x: auto; padding: 10px; }
  .bc-agent { width: 240px; flex: none; border: 1px solid var(--line); }
  .bc-skel-row, .bc-rail-empty { flex: none; }
}
@media (max-width: 560px) {
  .bc-date, .bc-date input { width: 100%; }
  .bc-date { flex: 1 1 140px; }
  .bc-time { margin-left: 0; width: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .bc-skel { animation: none; }
}
`;
